/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledSelect,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { useTomraMachines } from "../../store/useTomraMachines";
import { useDropLocations } from "../../store/useDropLocations";

type Inputs = {
  name?: string;
  serialNumber?: string;
  location?: string;
  status?: "available" | "not_available";
  unavailableUntil?: Date | null;
};

function AddTomraMachineScreen() {
  /** Hooks */
  const { dropLocations, getDropLocations } = useDropLocations();
  const { addTomraMachine, addedTomraMachine, clearAddedTomraMachine } =
    useTomraMachines();
  const navigate = useNavigate();
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      status: "available",
      unavailableUntil: null,
    },
  });

  const selectedStatus = watch("status");

  /** Effects */
  useEffect(() => {
    getDropLocations();
  }, []);

  useEffect(() => {
    if (addedTomraMachine) {
      clearAddedTomraMachine();
      navigate(-1);
    }
  }, [addedTomraMachine]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    addTomraMachine({
      ...data,
      unavailableUntil:
        data?.status === "not_available" && data?.unavailableUntil
          ? data.unavailableUntil.toISOString()
          : null,
    });
  };

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Register new Tomra Machine
        </h1>
      </div>
      <div className="flex flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New Tomra Machine
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Statiegeld Product name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.serialNumber)}
              label="Serial number"
              placeholder="Tomra machine serial number"
              name="serialNumber"
              rules={{ required: true }}
            />
            <ControlledSelect
              name="location"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.location)}
              label="Location"
              // @ts-ignore
              options={dropLocations?.map((location) => ({
                // @ts-ignore
                title: location?.cost_place as string,
                // @ts-ignore
                _id: location._id as string,
              }))}
              placeholder="Pick location here"
            />
            <ControlledSelect
              name="status"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.status)}
              label="Machine status"
              options={[
                { _id: "available", title: "Available" },
                { _id: "not_available", title: "Not available" },
              ]}
              placeholder="Pick status"
            />
            {selectedStatus === "not_available" ? (
              <div className="mb-4">
                <ControlledDatePicker
                  name="unavailableUntil"
                  control={control}
                  rules={{ required: false }}
                  error={Boolean(errors?.unavailableUntil)}
                  label="Unavailable until"
                  placeholder="Pick unavailable until date"
                />
                <SecondaryButton
                  type="button"
                  onClick={() => setValue("unavailableUntil", null)}
                  label="Clear unavailable date"
                />
              </div>
            ) : null}
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddTomraMachineScreen);
